import i18n from '@/utils/i18n';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { CREDIT_REQUEST_REASON_FRAGMENT } from '@/hooks/credit-requests/credit-request-queries';
import {
  CreditRequest_SearchVariantPacksInvoicesFragment,
  GetCreditRequestReasonFieldsFragment,
  GetCreditRequestReasonsQuery,
  GetCreditRequestReasonsQueryVariables,
} from '@/types';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { getTranslationValue } from '@/utils/translations';
import { gql } from '@apollo/client';
import { List } from '@/components/list';
import { ListOption } from '@/components/list-item';
import { StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { TYPOGRAPHY } from '@/styles';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import {
  useDynamicConfigValue,
  useSupplierDynamicConfigValue,
} from '@/hooks/use-dynamic-config-value';
import { useIsOverridingIneligibleItem } from '@/hooks/use-is-overriding-ineligible-item';
import { useOfflineCreditRequestData } from '@/hooks/use-offline-data';
import { useOfflineModeActive } from '@/hooks/use-offline-mode-active';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

interface Props {
  eligibleForCredit: boolean;
  guaranteed?: boolean;
  invoiceQuantityMap?: Record<string, number>;
  invoices: CreditRequest_SearchVariantPacksInvoicesFragment[];
  mostRecentInvoiceDate?: string;
  onSelectReason: (reason: GetCreditRequestReasonFieldsFragment) => void;
}

export const GET_CREDIT_REQUEST_REASONS_QUERY = gql`
  query GetCreditRequestReasons($supplierUUID: uuid!) {
    credit_request_reasons(where: { supplier_uuid: { _eq: $supplierUUID } }) {
      ...GetCreditRequestReasonFields
    }
  }

  ${CREDIT_REQUEST_REASON_FRAGMENT}
`;

// Mirrors shishito's CreditRequestType; the API can return every member even
// though only Guarantee is compared here.
enum CreditRequestType {
  Delivery = 'DELIVERY',
  Guarantee = 'GUARANTEE',
}

export interface CreditRequestReasonEligibilityParams {
  guaranteed?: boolean;
  invoiceQuantityMap?: Record<string, number>;
  invoices: CreditRequest_SearchVariantPacksInvoicesFragment[];
  isOverridingIneligibleItem: boolean | undefined;
  mostRecentInvoiceDate?: string;
}

export const isCreditRequestReasonDisabled = (
  reason: GetCreditRequestReasonFieldsFragment,
  {
    guaranteed,
    invoiceQuantityMap,
    invoices,
    isOverridingIneligibleItem,
    mostRecentInvoiceDate,
  }: CreditRequestReasonEligibilityParams,
): boolean => {
  if (isOverridingIneligibleItem) {
    return false;
  }

  const hasGuaranteeInformation = guaranteed !== undefined;
  const itemNotGuaranteed = hasGuaranteeInformation && !guaranteed;
  const noInvoices = invoices.length === 0;
  const eligibilityPeriod = reason.request_period_days;

  if (reason.invoice_required && !noInvoices) {
    const everyInvoiceHasNoAvailableQuantity = invoices.every(
      (invoice) => invoice.available_quantity_for_credit === 0,
    );

    if (
      everyInvoiceHasNoAvailableQuantity ||
      (invoiceQuantityMap &&
        invoices.every(
          ({ id, available_quantity_for_credit }) =>
            invoiceQuantityMap[id] &&
            available_quantity_for_credit &&
            invoiceQuantityMap[id] === available_quantity_for_credit,
        ))
    ) {
      return true;
    }
  }

  return Boolean(
    (itemNotGuaranteed && reason.type === CreditRequestType.Guarantee) ||
      (reason.invoice_required && noInvoices) ||
      (eligibilityPeriod &&
        (!mostRecentInvoiceDate ||
          moment(mostRecentInvoiceDate)
            .add(eligibilityPeriod, 'days')
            .isSameOrBefore(moment(), 'day'))),
  );
};

interface UseCreditRequestReasonsParams {
  restaurantUUID: string;
  supplierUUID: string;
}

export const useCreditRequestReasons = ({
  restaurantUUID,
  supplierUUID,
}: UseCreditRequestReasonsParams) => {
  const isOfflineModeActive = useOfflineModeActive();
  const { sort_reasons_alphabetically: sortReasonsAlphabetically } =
    useSupplierDynamicConfigValue('credit_request_config') ?? {};
  const { data: offlineCreditRequestData } = useOfflineCreditRequestData(
    restaurantUUID,
    supplierUUID,
  );

  const { data, loading } = useWrappedQuery<
    GetCreditRequestReasonsQuery,
    GetCreditRequestReasonsQueryVariables
  >(GET_CREDIT_REQUEST_REASONS_QUERY, {
    skip: isOfflineModeActive,
    variables: { supplierUUID },
  });

  const creditRequestReasons = useMemo(() => {
    const reasons = isOfflineModeActive
      ? offlineCreditRequestData?.getCreditRequestVariantPackEligibility.credit_request_reasons.map(
          (reason) => reason.credit_request_reason!,
        )
      : data?.credit_request_reasons;

    const options = (reasons || []).map((reason) => {
      return {
        ...reason,
        title: getTranslationValue(reason.title.translation),
        value: reason.uuid,
      };
    });

    if (sortReasonsAlphabetically) {
      options.sort((a, b) =>
        a.title.localeCompare(b.title, i18n.locale, { sensitivity: 'base' }),
      );
    }

    return options;
  }, [
    data,
    isOfflineModeActive,
    offlineCreditRequestData,
    sortReasonsAlphabetically,
  ]);

  return { creditRequestReasons, loading };
};

const WrappedReasonMainContent = (option: ListOption) => (
  <View style={styles.content}>
    <Text style={TYPOGRAPHY.listTitle}>{option.title}</Text>
  </View>
);

export const CreditRequestReasonSelection: React.FC<Props> = ({
  eligibleForCredit,
  guaranteed,
  invoices,
  invoiceQuantityMap,
  mostRecentInvoiceDate,
  onSelectReason,
}) => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const isOverridingIneligibleItem =
    useIsOverridingIneligibleItem(eligibleForCredit);
  const windowDimensions = useWindowDimensions();
  const { wrap_reason_text_enabled: wrapReasonTextEnabled } =
    useDynamicConfigValue('credit_request_config') ?? {};

  const { creditRequestReasons, loading } = useCreditRequestReasons({
    restaurantUUID,
    supplierUUID,
  });

  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.6);

  const isReasonDisabled = useCallback(
    (reason: GetCreditRequestReasonFieldsFragment) =>
      isCreditRequestReasonDisabled(reason, {
        guaranteed,
        invoiceQuantityMap,
        invoices,
        isOverridingIneligibleItem,
        mostRecentInvoiceDate,
      }),
    [
      invoices,
      invoiceQuantityMap,
      isOverridingIneligibleItem,
      guaranteed,
      mostRecentInvoiceDate,
    ],
  );

  return !loading ? (
    <>
      <Typography variant="heading-lg">
        {i18n.t('credit_request.form.select_reason.title')}
      </Typography>
      <List
        data={creditRequestReasons}
        listItemStyle={(option) =>
          isReasonDisabled(option) ? styles.disabledListItem : null
        }
        onSelect={(option) => {
          if (!isReasonDisabled(option)) {
            onSelectReason(option);
          }
        }}
        renderMainContent={
          wrapReasonTextEnabled ? WrappedReasonMainContent : undefined
        }
        style={[styles.list, { maxHeight: actionSheetMaxHeight }]}
      />
    </>
  ) : (
    <ActivityIndicator />
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  disabledListItem: {
    opacity: 0.4,
  },
  list: {
    marginHorizontal: -DEFAULT_PADDING,
    marginTop: 8,
  },
});
